/**
 * App-level provider composition. Wraps the tree with:
 *   - ThemeProvider — `@sill/ui` token consumer + localStorage persistence
 *   - SiteProvider — `sites[]`, `activeSite`, mutations against the mock API
 *   - SampleTrafficProvider — per-site sample-traffic toggle (ADR-0003 §"Sample-traffic mode")
 *   - ToastProvider — bottom-right ephemeral notifications (ported from
 *     `Foyer_Dashboard_CURRENT.jsx` line 460)
 *   - ApiProvider — the typed `SillApi` bag; the mock is mounted here in
 *     Phase 1, replaced by an HTTP client in Phase 2.
 *
 * Each context is exposed via a corresponding `use*()` hook for ergonomic
 * call-site usage. The mock API is created exactly once at module scope so
 * stateful in-memory operations (add site, enable sample traffic) survive
 * provider re-renders within a single page load.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Check, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import type { Site, SiteId } from '@sill/types';

import { ApiProvider, useApi } from '../api/api-context.js';
import type { SillApi } from '../api/contract.js';
import { createHttpClient } from '../api/http-client.js';
import { HttpProvider } from '../api/http-context.js';
import { createRealAgentsApi } from '../api/real-agents.js';
import { createRealAuditApi } from '../api/real-audit.js';
import { createRealAuthApi } from '../api/real-auth.js';
import { createRealAgentRollupsApi } from '../api/real-agent-rollups.js';
import { createRealBenchmarksApi } from '../api/real-benchmarks.js';
import { createRealMandatesApi } from '../api/real-mandates.js';
import { createRealPolicyApi } from '../api/real-policy.js';
import { createRealProfileExtractionApi } from '../api/real-profile-extraction.js';
import { createRealSiteScanApi } from '../api/real-site-scan.js';
import { createRealVisibilityApi } from '../api/real-visibility.js';
import { createRealReportingApi } from '../api/real-reporting.js';
import { createRealSitesApi } from '../api/real-sites.js';
import { createRealSkillsApi } from '../api/real-skills.js';
import { AgencyProvider } from './agency-context.js';
import { AuthProvider, isAnonymousAuthPath, useAuth } from './auth-context.js';
import { DryRunProvider } from './dry-run-context.js';
import { TimeZoneProvider } from './time-zone-context.js';
import { PendingEscalationsProvider } from './pending-escalations-context.js';
import { API_ORIGIN } from './env.js';
import type { SetView } from './view-router.js';
import { ThemeProvider, useC } from '../lib/theme.js';
import { isFreshSite } from '../lib/is-fresh-site.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

// ---------------------------------------------------------------------------
// SiteCtx
// ---------------------------------------------------------------------------

export interface SiteCtxValue {
  sites: Site[];
  activeSite: Site | null;
  setActiveSite: (site: Site | null) => void;
  addSite: (site: Site) => void;
  updateSite: (id: SiteId, patch: Partial<Site>) => void;
  refresh: () => Promise<void>;
  // ADR-0051 §2.2 — "Show deleted sites" affordance. When `true`,
  // `sites[]` includes soft-deleted (`deleted_at !== null`) archive
  // rows so the merchant can route into a deleted site's
  // append-only audit records (which the soft-delete preserved on
  // the server). Persisted in localStorage so the toggle stays
  // sticky across reloads. Default `false` preserves today's
  // active-only behavior.
  showDeletedSites: boolean;
  setShowDeletedSites: (next: boolean) => void;
  /**
   * `true` after the first `sites.list()` has settled (either with a
   * list or with an error). Consumers like `SitesList` key the
   * "No sites added" empty copy off this so the first load never
   * flashes the empty state before the server has responded
   * (founder 2026-10-20).
   *
   * Optional on the interface so existing test scaffolds that build
   * a minimal `SiteCtx.Provider` value continue to type-check; the
   * live SiteProvider always sets it. Consumers should treat
   * `undefined` as "already loaded" (back-compat).
   */
  loaded?: boolean;
}

// Exported for test scaffolding so component tests can wrap a
// `<ConnectBackendStep />` with a stub site/activeSite value without
// pulling in the whole real SiteProvider (which depends on the API
// bag and would re-issue `sites.list()`). Production code uses the
// `useSites()` hook below; tests provide via `<SiteCtx.Provider>`.
export const SiteCtx = createContext<SiteCtxValue | null>(null);

export function useSites(): SiteCtxValue {
  const ctx = useContext(SiteCtx);
  if (!ctx) {
    throw new Error('useSites() called outside <SiteProvider>');
  }
  return ctx;
}

/**
 * Optional variant for component scaffolds (tests, screenshot
 * harnesses, embeddable widgets) that render a view without the full
 * SiteProvider. Returns `null` instead of throwing; callers degrade
 * gracefully — Account's Notifications tab, for instance, hides its
 * per-site deep-link list when sites are unavailable.
 */
export function useOptionalSites(): SiteCtxValue | null {
  return useContext(SiteCtx);
}

// Per ADR-0010 §3.2 the backend returns `site_key` ONLY on create —
// `list`, `get`, and `verify` omit it (server stores only the hash).
// Anything that calls `setActiveSite(siteFromVerify)` would therefore
// drop the key from memory. We persist keys per-`site_id` in the browser
// so they survive (a) the verify→install transition, (b) page reloads,
// (c) the TopBar site switcher, AND (d) closing/reopening the tab or
// coming back another day.
//
// localStorage, NOT sessionStorage (founder 2026-07-29): the original
// sessionStorage choice meant a closed tab / new session forgot the key
// forever — and since the server keeps only the hash, the merchant was
// then forced to REGENERATE (invalidating the key already deployed on
// their live site). That's a hostile cliff for a value that is NOT
// secret: the site_key is public — it ships in the embed
// (`data-site-key="…"`) on the merchant's own page. localStorage keeps
// it recoverable across sessions in this browser with no meaningful
// exposure delta. (Cross-device recovery would require the server to
// return the key, an ADR-0010 posture change — separate decision.)
const SITE_KEY_STORAGE_PREFIX = 'sill:site_key:';

function rememberSiteKey(site_id: string, key: string): void {
  try {
    localStorage.setItem(SITE_KEY_STORAGE_PREFIX + site_id, key);
  } catch {
    /* private mode / storage disabled — silently skip */
  }
}

function recallSiteKey(site_id: string): string | undefined {
  try {
    return localStorage.getItem(SITE_KEY_STORAGE_PREFIX + site_id) ?? undefined;
  } catch {
    return undefined;
  }
}

function enrichWithSiteKey(site: Site): Site {
  if (site.site_key) {
    rememberSiteKey(site.site_id, site.site_key);
    return site;
  }
  const remembered = recallSiteKey(site.site_id);
  return remembered ? ({ ...site, site_key: remembered } as Site) : site;
}

/**
 * localStorage key for the last-active site. Persisting just the
 * `site_id` (not the full Site object) keeps the storage value tiny
 * and lets us re-resolve against the current sites[] on each load —
 * so a stale site_id (e.g. deleted site) falls back gracefully to
 * sites[0] without ever rendering with stale data.
 */
const LAST_ACTIVE_SITE_STORAGE_KEY = 'sill:last_active_site_id';

/**
 * localStorage key for the "Show deleted sites" toggle (ADR-0051 §2.2).
 * Sticky across reloads so a merchant who opted into the archive view
 * stays opted-in. The value is the string `'1'` or `'0'`; absent
 * (first load) defaults to `false` (active-only list).
 */
const SHOW_DELETED_SITES_STORAGE_KEY = 'sill:show_deleted_sites';

function rememberShowDeletedSites(next: boolean): void {
  try {
    localStorage.setItem(SHOW_DELETED_SITES_STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* private mode / storage disabled — toggle still works in-session */
  }
}

function recallShowDeletedSites(): boolean {
  try {
    return localStorage.getItem(SHOW_DELETED_SITES_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function rememberLastActiveSite(site_id: string | null): void {
  try {
    if (site_id === null) {
      localStorage.removeItem(LAST_ACTIVE_SITE_STORAGE_KEY);
    } else {
      localStorage.setItem(LAST_ACTIVE_SITE_STORAGE_KEY, site_id);
    }
  } catch {
    /* private mode / storage disabled — degrade silently */
  }
}

function recallLastActiveSiteId(): string | null {
  try {
    return localStorage.getItem(LAST_ACTIVE_SITE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * ADR-0053 §1.1 — the Shopify OAuth callback redirects to
 * `/onboarding?connected=shopify&site=<site_id>`. The SiteProvider
 * reads `?site=<site_id>` on bootstrap so the onboarding shell mounts
 * with the just-connected site active. The site_id is validated against
 * the freshly-fetched list (RLS-scoped at the server) — a foreign id
 * never matches and the bootstrap falls through to the
 * last-active / first-site picker. The URL value alone is not
 * authoritative.
 */
const SITE_ID_URL_RE = /^[0-9A-HJ-NP-TV-Z]{26}$/;
/**
 * Which site is active after a sites-list fetch. An already-selected
 * site is never overridden. A null selection is only filled on the
 * FIRST settled fetch (page load): URL `?site=` → last-active hint →
 * first site.
 *
 * Every later fetch (the window-focus refresh, a post-redirect
 * refresh) leaves a null selection null. Null is deliberate there:
 * "Add site" clears the active site to open a blank Add step, and
 * re-picking on focus jumped the merchant into their FIRST site's
 * onboarding and scan instead (founder 2026-10-27: clicking back
 * into the browser from a terminal re-selected siteforbiz.com).
 */
export function pickActiveSite<T extends { site_id: string }>(
  cur: T | null,
  next: readonly T[],
  opts: { initial: boolean; from_url: string | null; remembered_id: string | null },
): T | null {
  if (cur !== null) return cur;
  if (!opts.initial) return null;
  if (opts.from_url !== null) {
    const matched = next.find((s) => s.site_id === opts.from_url);
    if (matched !== undefined) return matched;
  }
  if (opts.remembered_id !== null) {
    const remembered = next.find((s) => s.site_id === opts.remembered_id);
    if (remembered !== undefined) return remembered;
  }
  return next[0] ?? null;
}

function recallSiteIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const id = new URL(window.location.href).searchParams.get('site');
    if (id === null) return null;
    return SITE_ID_URL_RE.test(id) ? id : null;
  } catch {
    return null;
  }
}

export function SiteProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useApi();
  const [sites, setSites] = useState<Site[]>([]);
  const [activeSite, setActiveSiteRaw] = useState<Site | null>(null);
  // ADR-0051 §2.2 — "Show deleted sites" affordance. Lazily seeded
  // from localStorage so the toggle survives reloads. Flipping it
  // re-issues the sites list with the appropriate `include_deleted`
  // value via the refresh effect below.
  const [showDeletedSites, setShowDeletedSitesRaw] = useState<boolean>(() =>
    recallShowDeletedSites(),
  );
  // Founder 2026-10-20: track whether the first list fetch has settled
  // (success OR error). Consumers key the "No sites added" copy off
  // this so an empty state never flashes before the server responds.
  const [loaded, setLoaded] = useState<boolean>(false);
  // Set once the first sites list lands; after that a null activeSite
  // is a deliberate choice ("Add site"), not "not restored yet".
  const initialSelectionDoneRef = useRef<boolean>(false);

  const setActiveSite = useCallback((site: Site | null): void => {
    rememberLastActiveSite(site?.site_id ?? null);
    setActiveSiteRaw(site === null ? null : enrichWithSiteKey(site));
  }, []);

  const setShowDeletedSites = useCallback((next: boolean): void => {
    rememberShowDeletedSites(next);
    setShowDeletedSitesRaw(next);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    // ADR-0051 §2.2 — when the toggle is on, request the widened
    // list so soft-deleted sites surface in the switcher / Mandates
    // selector. Off (default) preserves today's active-only contract.
    // The server still enforces RLS + tenancy; `include_deleted`
    // cannot widen beyond the requesting account.
    //
    // One retry after 1.5 s mirrors the auth-context pattern: a cold
    // Fly.io machine may not respond to the first request in time,
    // leaving sites = [] and the dashboard looking blank until the
    // next reload. 401s are NOT retried — the HTTP client's
    // on_unauthorized fires and routes to login before this catch runs.
    let next: Awaited<ReturnType<typeof api.sites.list>>;
    try {
      next = await api.sites.list({ include_deleted: showDeletedSites });
    } catch {
      try {
        await new Promise<void>((r) => setTimeout(r, 1500));
        next = await api.sites.list({ include_deleted: showDeletedSites });
      } catch (e) {
        // Even on failure, flip `loaded` so consumers stop rendering
        // the loading skeleton — they'll fall through to the empty /
        // error state the shell renders instead of blocking forever.
        setLoaded(true);
        throw e;
      }
    }
    setSites(next.map(enrichWithSiteKey));
    setLoaded(true);
    // Restore the active site on page load with this precedence:
    //   1. Already-set activeSite (mid-session) — never override.
    //   2. ADR-0053 §1.1 — `?site=<site_id>` from the URL (the OAuth
    //      callback's post-redirect carries this). The id is verified
    //      against the just-fetched server list so a foreign id never
    //      escalates; URL is a trigger, never authority.
    //   3. Last-active-site localStorage hint (cross-reload stickiness).
    //   4. First site in the list (brand-new visitor).
    // Steps 2-4 run on the first fetch only — see `pickActiveSite`.
    const initial = !initialSelectionDoneRef.current;
    initialSelectionDoneRef.current = true;
    const from_url = initial ? recallSiteIdFromUrl() : null;
    const remembered_id = initial ? recallLastActiveSiteId() : null;
    setActiveSiteRaw((cur) => {
      const picked = pickActiveSite(cur, next, { initial, from_url, remembered_id });
      return picked === null || picked === cur ? picked : enrichWithSiteKey(picked);
    });
  }, [api, showDeletedSites]);

  // Only fetch once a session exists: on /welcome and /verify there is
  // no cookie yet, and the resulting 401 used to fire the global
  // unauthorized hook.
  const auth_for_sites = useAuth();
  useEffect(() => {
    if (auth_for_sites.state.kind !== 'authenticated') return;
    void refresh();
  }, [refresh, auth_for_sites.state.kind]);

  // Founder feedback 2026-10-17: after redeploying the Cloudflare
  // Worker from Settings → Install, the "update available" dot
  // persisted until a full page refresh. The redeploy handler already
  // kicks sites.refresh() on success, but a merchant who ran the
  // redeploy in another tab (or who left the dashboard idle for a
  // while) still saw a stale flag. Refetching on window focus is
  // cheap (one list call, bounded by RLS), picks up any out-of-band
  // state change, and matches the pattern the Dashboard view uses
  // for its own panels.
  useEffect(() => {
    if (auth_for_sites.state.kind !== 'authenticated') return;
    if (typeof window === 'undefined') return;
    const onFocus = (): void => {
      // Deliberately fire-and-forget: a transient failure (offline,
      // 503) is handled by the retry in `refresh` itself; a stale
      // list is still safer than a crash on a focus event.
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh, auth_for_sites.state.kind]);

  const addSite = useCallback((site: Site): void => {
    const enriched = enrichWithSiteKey(site);
    setSites((cur) => [...cur, enriched]);
  }, []);

  const updateSite = useCallback((id: SiteId, patch: Partial<Site>): void => {
    setSites((cur) =>
      cur.map((s) =>
        s.site_id === id
          ? enrichWithSiteKey({ ...s, ...patch, site_id: s.site_id } as Site)
          : s,
      ),
    );
    setActiveSiteRaw((cur) =>
      cur && cur.site_id === id
        ? enrichWithSiteKey({ ...cur, ...patch, site_id: cur.site_id } as Site)
        : cur,
    );
  }, []);

  const value = useMemo<SiteCtxValue>(
    () => ({
      sites,
      activeSite,
      setActiveSite,
      addSite,
      updateSite,
      refresh,
      showDeletedSites,
      setShowDeletedSites,
      loaded,
    }),
    [
      sites,
      activeSite,
      setActiveSite,
      addSite,
      updateSite,
      refresh,
      showDeletedSites,
      setShowDeletedSites,
      loaded,
    ],
  );

  return <SiteCtx.Provider value={value}>{children}</SiteCtx.Provider>;
}

// ---------------------------------------------------------------------------
// SampleTrafficCtx — per-site, hidden on non-fresh sites
// ---------------------------------------------------------------------------

export interface SampleTrafficCtxValue {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  /** Whether the toggle should render at all. */
  visible: boolean;
}

/**
 * Exported so tests can prime the context without lighting up the full
 * `SampleTrafficProvider` (which requires a `SitesCtx` + a live api
 * client). Production code should consume via `useSampleTraffic()`.
 */
export const SampleTrafficCtx = createContext<SampleTrafficCtxValue>({
  enabled: false,
  setEnabled: () => {
    /* default no-op */
  },
  visible: false,
});

export function useSampleTraffic(): SampleTrafficCtxValue {
  return useContext(SampleTrafficCtx);
}

function SampleTrafficProvider({ children }: { children: ReactNode }): JSX.Element {
  const api = useApi();
  const { activeSite } = useSites();
  const [bySite, setBySite] = useState<Record<string, boolean>>({});

  // ADR-0115 — the toggle is now GLOBAL: visible on every active site,
  // not just on a fresh one. A non-fresh site with quiet real data is
  // still a valid "what would this look like" preview moment. The
  // toggle defaults ON for fresh sites (the pre-ADR-0115 behaviour, so
  // a brand-new merchant lands on a populated demo) and OFF for sites
  // with real traffic (so a working dashboard is the honest default).
  const siteId = activeSite?.site_id;
  const freshInput = activeSite
    ? {
        calls: 0,
        status: activeSite.status,
        added_at: activeSite.created_at,
      }
    : null;
  const fresh = isFreshSite(freshInput);
  const visible = activeSite !== null && activeSite !== undefined;
  const explicit = siteId ? bySite[siteId] : undefined;
  // Default-on for fresh sites; default-off otherwise. `explicit` (the
  // merchant's per-site flip) always wins.
  const enabled = visible ? (explicit ?? fresh) : false;

  const setEnabled = useCallback(
    (next: boolean): void => {
      if (!siteId) return;
      setBySite((cur) => ({ ...cur, [siteId]: next }));
      void api.sites.enableSampleTraffic(siteId, next);
    },
    [api, siteId],
  );

  const value = useMemo<SampleTrafficCtxValue>(
    () => ({ enabled, setEnabled, visible }),
    [enabled, setEnabled, visible],
  );

  return <SampleTrafficCtx.Provider value={value}>{children}</SampleTrafficCtx.Provider>;
}

// ---------------------------------------------------------------------------
// ToastCtx — ported from Foyer_Dashboard_CURRENT.jsx line 460
// ---------------------------------------------------------------------------

export type ToastTone = 'lime' | 'mint' | 'amber' | 'coral';

export interface ToastInput {
  title?: string;
  message?: string;
  body?: string;
  tone?: ToastTone;
}

interface ToastInternal extends ToastInput {
  id: string;
}

// Exported for test scaffolding (same rationale as SiteCtx above).
export const ToastCtx = createContext<(t: ToastInput) => void>(() => {
  /* default no-op */
});

export function useToast(): (t: ToastInput) => void {
  return useContext(ToastCtx);
}

function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const C = useC();
  const [toasts, setToasts] = useState<ToastInternal[]>([]);

  const push = useCallback((t: ToastInput): void => {
    const id = Math.random().toString(36).slice(2);
    setToasts((cur) => [...cur, { ...t, id }]);
    setTimeout(() => {
      setToasts((cur) => cur.filter((x) => x.id !== id));
    }, 4000);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="fixed bottom-4 right-4 z-[300] space-y-2 max-w-sm">
        <AnimatePresence>
          {toasts.map((t) => {
            const title = t.title ?? t.message ?? '';
            const body = t.body;
            const toneMap: Record<
              ToastTone,
              { border: string; icon: typeof Check; iconColor: string }
            > = {
              lime: { border: C.limeFg, icon: Check, iconColor: C.limeFg },
              mint: { border: C.mintFg, icon: Check, iconColor: C.mintFg },
              amber: { border: C.amberFg, icon: AlertTriangle, iconColor: C.amberFg },
              coral: { border: C.coralFg, icon: X, iconColor: C.coralFg },
            };
            const t2 = toneMap[t.tone ?? 'lime'];
            const ToneIcon = t2.icon;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 20, x: 20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="px-4 py-3 flex items-start gap-3"
                style={{
                  background: C.surface,
                  border: `1px solid ${t2.border}`,
                  borderRadius: 3,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  minWidth: 280,
                }}
              >
                <ToneIcon size={14} color={t2.iconColor} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      fontFamily: sansFont,
                      fontSize: 13,
                      fontWeight: 500,
                      color: C.text,
                      lineHeight: 1.4,
                    }}
                  >
                    {title}
                  </div>
                  {body ? (
                    <div
                      style={{
                        fontFamily: sansFont,
                        fontSize: 12,
                        color: C.muted,
                        marginTop: 2,
                        lineHeight: 1.4,
                      }}
                    >
                      {body}
                    </div>
                  ) : null}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastCtx.Provider>
  );
}

// Acknowledge the mono font import — used by inline-styled mono labels in
// child providers; export prevents an unused-import warning in strict mode.
export const _mono = monoFont;

// ---------------------------------------------------------------------------
// Composed root provider — every bag is real per ADR-0019 + ADR-0020.
// The `mandates` bag (ADR-0020) routes through the same `/v1/audit`
// endpoint that the `audit` bag uses, with a `decision_class=discovery`
// filter applied server-side and a defense-in-depth Discovery filter
// applied client-side. The `__tests__/fixtures/mock-api.ts` file is
// now strictly test-only — no production imports remain.
//
// The on_unauthorized callback flows from the HTTP client →
// AuthProvider → setView('login') so an in-flight 401 (session expired
// mid-session) uniformly routes to the sign-in view, regardless of
// which bag issued the request.
// ---------------------------------------------------------------------------

let onUnauthorizedHook: (() => void) | null = null;
const httpClient = createHttpClient({
  api_origin: API_ORIGIN,
  on_unauthorized: () => {
    if (onUnauthorizedHook !== null) {
      onUnauthorizedHook();
    }
  },
});

const apiInstance: SillApi = {
  sites: createRealSitesApi({ http: httpClient }),
  skills: createRealSkillsApi({ http: httpClient }),
  audit: createRealAuditApi({ http: httpClient }),
  agents: createRealAgentsApi({ http: httpClient }),
  auth: createRealAuthApi({ http: httpClient }),
  mandates: createRealMandatesApi({ http: httpClient }),
  policy: createRealPolicyApi({ http: httpClient }),
  profile_extraction: createRealProfileExtractionApi({ http: httpClient }),
  scan: createRealSiteScanApi({ http: httpClient }),
  visibility: createRealVisibilityApi({ http: httpClient }),
  reporting: createRealReportingApi({ http: httpClient }),
  benchmarks: createRealBenchmarksApi({ http: httpClient }),
  agentRollups: createRealAgentRollupsApi({ http: httpClient }),
};

export interface AppProvidersProps {
  children: ReactNode;
  /**
   * View-control callback — the AuthProvider's bootstrap auth check
   * routes to 'login' on 401 / service-error, and the HTTP client's
   * on_unauthorized callback fires the same routing on in-flight
   * 401s. Same shape as the other view-control callbacks elsewhere
   * in the tree.
   */
  setView: SetView;
}

export function AppProviders({ children, setView }: AppProvidersProps): JSX.Element {
  // Wire the on_unauthorized hook to the AuthProvider's clear + view
  // routing. The module-scope variable lets the HTTP client (which
  // is itself module-scope) reach into the latest mounted callback
  // without a re-instantiation per render.
  useEffect(() => {
    onUnauthorizedHook = (): void => {
      // A 401 on a pre-cookie surface (/welcome confirm, /verify bridge)
      // is expected: the providers' mount-time fetches run before the
      // visitor has a session. Routing to Login there replaced the
      // confirm page with the sign-in form and blocked every new signup
      // (2026-09-24 — docs/runbooks/magic-link-and-signup-failures.md
      // §1.1). INVARIANT: never remove this guard; any new pre-cookie
      // page goes into `isAnonymousAuthPath`.
      if (typeof window !== 'undefined' && isAnonymousAuthPath(window.location.pathname)) return;
      setView('login');
    };
    return (): void => {
      onUnauthorizedHook = null;
    };
  }, [setView]);

  return (
    <ThemeProvider>
      <ApiProvider api={apiInstance}>
        <HttpProvider http={httpClient}>
          <AuthProvider setView={setView}>
            <AgencyProvider>
              <SiteProvider>
                <TimeZoneProvider>
                  <PendingEscalationsProvider>
                    <DryRunProvider>
                      <SampleTrafficProvider>
                        <ToastProvider>{children}</ToastProvider>
                      </SampleTrafficProvider>
                    </DryRunProvider>
                  </PendingEscalationsProvider>
                </TimeZoneProvider>
              </SiteProvider>
            </AgencyProvider>
          </AuthProvider>
        </HttpProvider>
      </ApiProvider>
    </ThemeProvider>
  );
}
